import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSync,
  faDatabase,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faShieldAlt,
  faQrcode,
  faLock,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import { assetApi } from '../api/assetApi';
import { authApi } from '../api/authApi';
import { marketDataApi } from '../api/marketDataApi';
import { useAuth } from '../contexts/AuthContext';
import ThemePicker from './ThemePicker';

const PROVIDER_LABELS = {
  quotes: 'Quotes',
  bars: 'Price history / bars',
  options: 'Options chains',
  movers: 'Market movers',
  instruments: 'Instrument search',
};

const Settings = () => {
  const { user, setUser, maskAmounts, setMaskAmounts } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.twoFactorEnabled || false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorSuccess, setTwoFactorSuccess] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [twoFactorToken, setTwoFactorToken] = useState('');

  const [mdProviders, setMdProviders] = useState({
    quotes: 'alpaca',
    bars: 'alpaca',
    options: 'alpaca',
    movers: 'alpaca',
    instruments: 'alpaca',
  });
  const [mdCapabilities, setMdCapabilities] = useState({});
  const [mdAvailable, setMdAvailable] = useState({ alpaca: true, yahoo: true, schwab: false });
  const [mdLoading, setMdLoading] = useState(true);
  const [mdSaving, setMdSaving] = useState(false);
  const [mdMessage, setMdMessage] = useState(null);
  const [mdError, setMdError] = useState(null);

  useEffect(() => {
    loadStats();
    loadMarketDataProviders();
  }, []);

  useEffect(() => {
    setTwoFactorEnabled(user?.twoFactorEnabled || false);
  }, [user]);

  const loadMarketDataProviders = async () => {
    try {
      setMdLoading(true);
      setMdError(null);
      const status = await marketDataApi.getProviders();
      if (status?.providers) setMdProviders(status.providers);
      if (status?.capabilities) setMdCapabilities(status.capabilities);
      if (status?.available) setMdAvailable(status.available);
    } catch (err) {
      console.error('Failed to load market data providers:', err);
      setMdError(err.response?.data?.error || err.message || 'Failed to load providers');
    } finally {
      setMdLoading(false);
    }
  };

  const saveMarketDataProviders = async () => {
    try {
      setMdSaving(true);
      setMdError(null);
      setMdMessage(null);
      await authApi.savePreferences({ marketDataProviders: mdProviders });
      await loadMarketDataProviders();
      setMdMessage('Market data providers saved.');
    } catch (err) {
      setMdError(err.response?.data?.error || err.message || 'Failed to save providers');
    } finally {
      setMdSaving(false);
    }
  };
  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const response = await assetApi.getAssetStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load asset stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSyncAssets = async () => {
    if (!window.confirm('This will sync all available assets from Alpaca API to the database. This may take a few minutes. Continue?')) {
      return;
    }

    try {
      setSyncing(true);
      setSyncError(null);
      setSyncResult(null);
      
      const response = await assetApi.syncAssets();
      setSyncResult(response);
      
      // Reload stats after sync
      await loadStats();
    } catch (err) {
      setSyncError(err.response?.data?.error || err.message || 'Failed to sync assets');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="lt-card p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-ink mb-1">Appearance</h2>
          <p className="text-muted text-sm">
            Desk is the current theme. Robinhood adds a Legend-style black and green skin without replacing it.
          </p>
        </div>
        <ThemePicker />
      </div>

      {/* Asset Management */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            <FontAwesomeIcon icon={faDatabase} className="mr-2" />
            Asset Management
          </h2>
          <p className="text-gray-600 text-sm">
            Sync and manage available trading assets from Alpaca Markets API
          </p>
        </div>

      {/* Sync Statistics */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Database Statistics</h3>
        {loadingStats ? (
          <div className="flex items-center text-gray-500">
            <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
            Loading statistics...
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Total Assets</div>
              <div className="text-2xl font-bold text-indigo-600">{stats.total?.toLocaleString() || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Active Assets</div>
              <div className="text-2xl font-bold text-green-600">{stats.active?.toLocaleString() || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Tradable Assets</div>
              <div className="text-2xl font-bold text-blue-600">{stats.tradable?.toLocaleString() || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Last Sync</div>
              <div className="text-sm font-medium text-gray-800">{formatDate(stats.lastSync)}</div>
            </div>
          </div>
        ) : (
          <div className="text-gray-500">No statistics available</div>
        )}
      </div>

      {/* Sync Section */}
      <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Sync Assets</h3>
        <p className="text-sm text-gray-600 mb-4">
          Fetch all available assets from Alpaca Markets API and save them to the local database.
          This operation may take several minutes depending on the number of assets.
        </p>
        
        <div className="flex items-center gap-4">
          <button
            onClick={handleSyncAssets}
            disabled={syncing}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center"
          >
            {syncing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSync} className="mr-2" />
                Sync Assets Now
              </>
            )}
          </button>
          
          <button
            onClick={loadStats}
            disabled={loadingStats}
            className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loadingStats ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                Refreshing...
              </>
            ) : (
              'Refresh Stats'
            )}
          </button>
        </div>
      </div>

      {/* Sync Result */}
      {syncResult && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2 mt-1" />
            <div className="flex-1">
              <div className="font-semibold text-green-800 mb-2">{syncResult.message}</div>
              {syncResult.stats && (
                <div className="text-sm text-green-700 space-y-1">
                  <div>• Total processed: {syncResult.stats.total?.toLocaleString()}</div>
                  <div>• New assets inserted: {syncResult.stats.inserted?.toLocaleString()}</div>
                  <div>• Existing assets updated: {syncResult.stats.updated?.toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sync Error */}
      {syncError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-2 mt-1" />
            <div className="flex-1">
              <div className="font-semibold text-red-800 mb-1">Sync Failed</div>
              <div className="text-sm text-red-700">{syncError}</div>
            </div>
          </div>
        </div>
      )}

        {/* Info Section */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mr-2 mt-1" />
            <div className="flex-1 text-sm text-blue-800">
              <div className="font-semibold mb-1">About Asset Sync</div>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  Assets are fetched from Alpaca Markets API endpoint:{' '}
                  <code className="bg-blue-100 px-1 rounded">/v2/assets</code>
                </li>
                <li>Sync is performed on-demand only (not automatic)</li>
                <li>Existing assets are updated, new assets are inserted</li>
                <li>
                  Database is stored locally in{' '}
                  <code className="bg-blue-100 px-1 rounded">backend/data/assets.db</code>
                </li>
                <li>Sync may take several minutes for large datasets</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Market data providers */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            <FontAwesomeIcon icon={faChartLine} className="mr-2" />
            Market Data Providers
          </h2>
          <p className="text-gray-600 text-sm">
            Choose Alpaca, Schwab, or Yahoo (where supported) for each market-data capability.
            Schwab requires connecting your account on the Schwab page first.
          </p>
        </div>

        {mdLoading ? (
          <div className="flex items-center text-gray-500">
            <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
            Loading provider settings...
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 text-xs text-gray-600">
              <span className="px-2 py-1 rounded bg-gray-50 border border-gray-200">
                Alpaca: {mdAvailable.alpaca ? 'available' : 'unavailable'}
              </span>
              <span className="px-2 py-1 rounded bg-gray-50 border border-gray-200">
                Yahoo: {mdAvailable.yahoo ? 'available' : 'unavailable'}
              </span>
              <span className="px-2 py-1 rounded bg-gray-50 border border-gray-200">
                Schwab: {mdAvailable.schwab ? 'configured' : 'not configured'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.keys(PROVIDER_LABELS).map((cap) => {
                const options = mdCapabilities[cap] || ['alpaca'];
                return (
                  <label key={cap} className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      {PROVIDER_LABELS[cap]}
                    </span>
                    <select
                      value={mdProviders[cap] || 'alpaca'}
                      onChange={(e) =>
                        setMdProviders((prev) => ({ ...prev, [cap]: e.target.value }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {options.map((p) => (
                        <option key={p} value={p} disabled={p === 'schwab' && !mdAvailable.schwab}>
                          {p.charAt(0).toUpperCase() + p.slice(1)}
                          {p === 'schwab' && !mdAvailable.schwab ? ' (server not configured)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>

            {mdError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                {mdError}
              </div>
            )}
            {mdMessage && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                {mdMessage}
              </div>
            )}

            <button
              type="button"
              onClick={saveMarketDataProviders}
              disabled={mdSaving}
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium flex items-center disabled:opacity-50"
            >
              {mdSaving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save market data providers'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Security / Two-Factor Authentication & Privacy */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-1 flex items-center">
              <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-indigo-600" />
              Security & Privacy
            </h2>
            <p className="text-gray-600 text-sm">
              Protect your KyBTrading account and control how much information is visible on screen.
            </p>
          </div>
          <div>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                twoFactorEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <FontAwesomeIcon icon={faLock} className="mr-1" />
              {twoFactorEnabled ? '2FA Enabled' : '2FA Disabled'}
            </span>
          </div>
        </div>

        {/* Two-Factor Authentication */}
        {twoFactorError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
            {twoFactorError}
          </div>
        )}

        {twoFactorSuccess && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">
            {twoFactorSuccess}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">How it works</h3>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>Click <strong>Enable 2FA</strong> to generate a QR code.</li>
              <li>Scan the QR code with Google Authenticator (or any TOTP app).</li>
              <li>Enter the 6-digit code from the app to confirm.</li>
              <li>On next login, you’ll need your password and the 6-digit code.</li>
            </ul>
          </div>

          <div className="flex flex-col items-start space-y-3">
            {!twoFactorEnabled && !qrCodeDataUrl && (
              <button
                onClick={async () => {
                  try {
                    setTwoFactorError('');
                    setTwoFactorSuccess('');
                    setTwoFactorLoading(true);
                    const res = await authApi.startTwoFactorSetup();
                    setQrCodeDataUrl(res.data.qrCodeDataUrl);
                  } catch (err) {
                    setTwoFactorError(
                      err.response?.data?.error || err.message || 'Failed to start 2FA setup'
                    );
                  } finally {
                    setTwoFactorLoading(false);
                  }
                }}
                disabled={twoFactorLoading}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {twoFactorLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                    Preparing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                    Enable 2FA
                  </>
                )}
              </button>
            )}

            {qrCodeDataUrl && !twoFactorEnabled && (
              <div className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-center mb-2">
                  <FontAwesomeIcon icon={faQrcode} className="mr-2 text-gray-700" />
                  <span className="text-sm font-semibold text-gray-800">Scan this QR code</span>
                </div>
                <div className="flex flex-col items-center">
                  <img
                    src={qrCodeDataUrl}
                    alt="2FA QR Code"
                    className="w-40 h-40 object-contain mb-3 border border-gray-200 rounded"
                  />
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Enter 6-digit code from your Authenticator app
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={twoFactorToken}
                    onChange={(e) => setTwoFactorToken(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-2"
                    placeholder="123456"
                  />
                  <div className="flex items-center space-x-2 w-full">
                    <button
                      onClick={async () => {
                        try {
                          setTwoFactorError('');
                          setTwoFactorSuccess('');
                          setTwoFactorLoading(true);
                          const res = await authApi.verifyTwoFactor(twoFactorToken);
                          if (res.success) {
                            setTwoFactorEnabled(true);
                            setQrCodeDataUrl('');
                            setTwoFactorToken('');
                            setTwoFactorSuccess('Two-factor authentication enabled successfully.');
                            const updatedUser = { ...(user || {}), twoFactorEnabled: true };
                            setUser(updatedUser);
                            localStorage.setItem('user', JSON.stringify(updatedUser));
                          } else {
                            setTwoFactorError(res.error || 'Invalid verification code');
                          }
                        } catch (err) {
                          setTwoFactorError(
                            err.response?.data?.error || err.message || 'Failed to verify code'
                          );
                        } finally {
                          setTwoFactorLoading(false);
                        }
                      }}
                      disabled={twoFactorLoading || twoFactorToken.length < 6}
                      className="flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Verify & Enable
                    </button>
                    <button
                      onClick={() => {
                        setQrCodeDataUrl('');
                        setTwoFactorToken('');
                        setTwoFactorError('');
                        setTwoFactorSuccess('');
                      }}
                      type="button"
                      className="px-3 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {twoFactorEnabled && (
              <button
                onClick={async () => {
                  if (!window.confirm('Disable two-factor authentication for your account?')) {
                    return;
                  }
                  try {
                    setTwoFactorError('');
                    setTwoFactorSuccess('');
                    setTwoFactorLoading(true);
                    const res = await authApi.disableTwoFactor();
                    if (res.success) {
                      setTwoFactorEnabled(false);
                      setTwoFactorSuccess('Two-factor authentication disabled.');
                      const updatedUser = { ...(user || {}), twoFactorEnabled: false };
                      setUser(updatedUser);
                      localStorage.setItem('user', JSON.stringify(updatedUser));
                    } else {
                      setTwoFactorError(res.error || 'Failed to disable 2FA');
                    }
                  } catch (err) {
                    setTwoFactorError(
                      err.response?.data?.error || err.message || 'Failed to disable 2FA'
                    );
                  } finally {
                    setTwoFactorLoading(false);
                  }
                }}
                disabled={twoFactorLoading}
                className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {twoFactorLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faLock} className="mr-2" />
                    Disable 2FA
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Anonymous Mode */}
        <div className="pt-4 mt-2 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Anonymous Mode</h3>
          <p className="text-sm text-gray-700 mb-2">
            When enabled, your portfolio and balance amounts will be masked (shown as ***) in the
            dashboard and portfolio views. This is useful when sharing your screen.
          </p>
          <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={maskAmounts}
              onChange={(e) => setMaskAmounts(e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span>Enable anonymous mode (mask balances and portfolio amounts)</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default Settings;

